// src/components/blog/features/SortFilter.tsx

'use client'

import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import RiSortAsc from '~/icons/remix/generated/RiSortAsc'
import RiSortDesc from '~/icons/remix/generated/RiSortDesc'
import RiTimeLine from '~/icons/remix/generated/RiTimeLine'
import RiStarLine from '~/icons/remix/generated/RiStarLine'
type SortOption = {
  value: string
  label: string
  icon: React.ComponentType<{ className?: string }>
}

// NOTE: `value`s MUST match the archive's `validateSortParam` allowlist
// (`src/lib/utils/sanitization.ts`) — otherwise the server rejects the param and
// silently falls back to `latest`, so the control appears to do nothing.
const SORT_OPTIONS: SortOption[] = [
  { value: 'latest', label: 'Latest first', icon: RiSortDesc },
  { value: 'oldest', label: 'Oldest first', icon: RiSortAsc },
  { value: 'reading-time-asc', label: 'Quick reads', icon: RiTimeLine },
  { value: 'reading-time-desc', label: 'Long reads', icon: RiTimeLine },
  // The `trending` sort orders by the editorial `featured` flag (then newest) —
  // it is NOT an engagement/trending signal, so it's labelled "Featured" to
  // match what it actually does. (Value stays `trending` to keep the URL param /
  // `validateSortParam` contract stable.)
  { value: 'trending', label: 'Featured', icon: RiStarLine }
]

/**
 * Props for the SortFilter component
 * @interface SortFilterProps
 * @property {string} sort - The current sort value (e.g., 'latest', 'reading-time-asc')
 * @property {string} [className] - Optional CSS classes to apply to the select element
 */
interface SortFilterProps {
  sort: string
  className?: string
}

/**
 * A client-side component that renders a select dropdown for sorting blog posts
 * with various sort options including date, reading time, and popularity.
 *
 * @component
 * @param {SortFilterProps} props - The component props
 * @param {string} props.sort - The current sort value
 * @param {string} [props.className] - Optional CSS classes
 * @returns {JSX.Element} A select element with sort options
 *
 * @example
 * ```tsx
 * <SortFilter sort="latest" className="my-custom-class" />
 * ```
 */
export function SortFilter({ sort, className }: SortFilterProps): JSX.Element {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const selectedOption = SORT_OPTIONS.find(option => option.value === sort) ?? SORT_OPTIONS[0]
  const Icon = selectedOption?.icon ?? RiSortDesc

  return (
    <div className="relative inline-block">
      <select
        value={sort}
        onChange={(e) => {
          // Apply the sort via a client-side navigation instead of a full-page
          // reload (`window.location.href`), so the list re-renders with the new
          // `sort` searchParam while preserving scroll/app state.
          const params = new URLSearchParams(searchParams?.toString() ?? '')
          params.set('sort', e.target.value)
          router.push(`${pathname}?${params.toString()}`)
        }}
        className={`appearance-none rounded-lg border border-gray-200 bg-white pl-10 pr-10 py-2 text-sm font-medium text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-800 dark:bg-gray-900 dark:text-white ${className ?? ''}`}
      >
        {SORT_OPTIONS.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
        <Icon className="h-4 w-4 text-gray-500 dark:text-gray-400" aria-hidden="true" />
      </div>
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
        <svg className="h-4 w-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  )
}